import classNames from 'classnames';
import debounce from 'lodash.debounce';
import exact from 'prop-types-exact';
import PropTypes from 'prop-types';
import queryStringify from 'querystringify';
import React, { Component } from 'react';
import requireIf from 'react-required-if';
import throttle from 'lodash.throttle';

const propTypes = exact({
  blockName: PropTypes.string,
  focalPointLeft: PropTypes.number,
  focalPointTop: PropTypes.number,
  height: requireIf(PropTypes.number, (props) => props.useAspectRatio),
  lazyLoad: PropTypes.bool,
  reloadOnResize: PropTypes.bool,
  title: PropTypes.string,
  updateDate: PropTypes.string,
  url: PropTypes.string.isRequired,
  useAspectRatio: PropTypes.bool,
  width: requireIf(PropTypes.number, (props) => props.useAspectRatio),
});

const defaultProps = {
  blockName: null,
  focalPointLeft: 0.5,
  focalPointTop: 0.5,
  height: null,
  lazyLoad: true,
  reloadOnResize: true,
  title: null,
  updateDate: null,
  useAspectRatio: false,
  width: null,
};

class Image extends Component {
  constructor(props) {
    super(props);

    this.lazyLoad = this.lazyLoad.bind(this);
    this.loadImage = this.loadImage.bind(this);

    this.mounted = true;

    this.state = {
      height: null,
      width: null,
      isLoaded: false,
      styles: {},
    };
  }

  componentDidMount() {
    // Store connection quality
    const { connection, mozConnection, webkitConnection } = window.navigator;
    const navigatorConnection = connection || mozConnection || webkitConnection;
    if (navigatorConnection) this.isFastConnection = navigatorConnection.downlink > 5;
    else this.isFastConnection = true;

    // Store aspect ratio and initialise styles
    if (this.props.useAspectRatio) {
      this.aspectRatio = this.props.height / this.props.width;
      this.styles = { paddingTop: `${this.aspectRatio * 100}%` };
    }

    // Initialise image options
    this.options = {
      mode: 'crop',
      quality: this.isFastConnection ? 80 : 60,
      center: `${this.props.focalPointTop},${this.props.focalPointLeft}`,
      rnd: new Date(this.props.updateDate).getTime(),
    };

    // Lazy load image on window scroll
    if (this.props.lazyLoad) {
      this.lazyLoad();
      this.windowScrollThrottle = throttle(this.lazyLoad, 100);
      window.addEventListener('scroll', this.windowScrollThrottle);
    } else {
      this.loadImage();
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    if (this.windowScrollThrottle) window.removeEventListener('scroll', this.windowScrollThrottle);
    if (this.windowResizeDebounce) window.removeEventListener('resize', this.windowResizeDebounce);
  }

  lazyLoad() {
    if (!this.figure) return;
    // Check image is within 100px of viewport
    const { bottom, top } = this.figure.getBoundingClientRect();
    if (top > window.innerHeight + 200 || bottom < -200) return;

    // Load image and disable lazy load
    this.loadImage();
    window.removeEventListener('scroll', this.windowScrollThrottle);
    this.windowScrollThrottle = null;
  }

  loadImage() {
    if (!this.figure) return;
    // Calculate crop dimensions rounded to the nearest multiple of 64px
    // This will cap the number of requests and images cached on the server
    const snapTo = 64;
    const width = Math.ceil(this.figure.clientWidth / snapTo) * snapTo;
    let height = Math.ceil(this.figure.clientHeight / snapTo) * snapTo;
    if (this.props.useAspectRatio) height = Math.ceil(width * this.aspectRatio);

    // Cease request if image size is already loaded
    if (width === this.state.width && height === this.state.height) return;

    // Create image URL with query parameters
    const urlParameters = queryStringify.stringify({ ...this.options, width, height }, true);
    const url = `${this.props.url}${urlParameters}`;

    // Create inline styles
    const styles = { ...this.styles, backgroundImage: `url(${url})` };

    // Preload image then update inline style
    const img = document.createElement('img');
    const isLoaded = true;
    img.addEventListener(
      'load',
      () => this.mounted && this.setState({ width, height, styles, isLoaded })
    );
    img.src = url;

    // Enable image reload on window resize
    if (!this.windowResizeDebounce && this.props.reloadOnResize && this.isFastConnection) {
      this.windowResizeDebounce = debounce(this.loadImage, 200);
      window.addEventListener('resize', this.windowResizeDebounce);
    }
  }

  render() {
    const figureClasses = classNames(this.props.blockName, 'Image', {
      contain: this.props.useAspectRatio,
      loaded: this.state.isLoaded,
      unloaded: !this.state.isLoaded,
    });

    return (
      <figure
        ref={(element) => {
          this.figure = element;
        }}
        style={this.state.styles}
        className={figureClasses}
        aria-label={this.props.title}
        role="img"
      />
    );
  }
}

Image.propTypes = propTypes;
Image.defaultProps = defaultProps;

export default Image;
